// components/create/GuestListSelector.js
import React, { useState, useEffect } from 'react';
import '../../styles/GuestListSelector.scss';
import { getUserGuestLists } from '../../services/guestListService';
import Icon from '../Icon';
import Portal from '../Portal';

export default function GuestListSelector({
  visible,
  onClose,
  selectedGuestListIds = [],
  onGuestListsSelected,
  onCreateGuestList,
}) {
  const [guestLists, setGuestLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempSelected, setTempSelected] = useState([]);

  useEffect(() => {
    if (visible) {
      // Always reload guest lists when modal opens to get fresh contact counts
      loadGuestLists();
    }
  }, [visible]);

  // Separate effect to update selected lists
  useEffect(() => {
    if (visible) {
      setTempSelected(selectedGuestListIds);
    }
  }, [visible, selectedGuestListIds]);

  const loadGuestLists = async () => {
    try {
      setLoading(true);
      const lists = await getUserGuestLists();
      console.log('📋 Loaded guest lists:', lists.length, 'lists');
      // Log contact counts for debugging
      lists.forEach(list => {
        console.log(`  - ${list.name}: ${list.contact_count || 0} contacts`);
      });
      setGuestLists(lists);
    } catch (error) {
      console.error('Error loading guest lists:', error);
      alert('Failed to load guest lists');
    } finally {
      setLoading(false);
    }
  };

  const toggleGuestList = (listId) => {
    if (tempSelected.includes(listId)) {
      setTempSelected(tempSelected.filter(id => id !== listId));
    } else {
      setTempSelected([...tempSelected, listId]);
    }
  };

  const handleDone = () => {
    onGuestListsSelected(tempSelected);
    onClose();
  };

  const handleCreateNew = () => {
    onClose();
    if (onCreateGuestList) {
      onCreateGuestList();
    }
  };

  if (!visible) return null;

  return (
    <Portal>
    <div className="modal-overlay" onClick={onClose}>
      <div className="guest-list-selector-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <button className="close-button" onClick={onClose}>
            <Icon name="close" />
          </button>
          <h2 className="modal-title">Select Guest Lists</h2>
          <button className="done-button" onClick={handleDone}>
            Done
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : guestLists.length === 0 ? (
            <div className="empty-container">
              <Icon name="people-outline" />
              <h3 className="empty-text">No Guest Lists</h3>
              <p className="empty-subtext">
                Create your first guest list to get started
              </p>
              <button className="create-button" onClick={handleCreateNew}>
                <Icon name="add" />
                <span>Create Guest List</span>
              </button>
            </div>
          ) : (
            <>
              <div className="list-content">
                {guestLists.map((item) => {
                  const isSelected = tempSelected.includes(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`list-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleGuestList(item.id)}
                    >
                      <div className="list-left">
                        <div className="list-icon">
                          <Icon name="people" />
                        </div>
                        <div className="list-info">
                          <p className="list-name">{item.name}</p>
                          <p className="list-count">
                            {item.contact_count || 0} {(item.contact_count || 0) === 1 ? 'contact' : 'contacts'}
                          </p>
                        </div>
                      </div>

                      <div className="checkbox">
                        {isSelected ? (
                          <div className="checkbox-selected">
                            <Icon name="checkmark" />
                          </div>
                        ) : (
                          <div className="checkbox-unselected"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Create New Button at Bottom */}
              <div className="bottom-section">
                <button className="create-new-button" onClick={handleCreateNew}>
                  <Icon name="add-circle-outline" />
                  <span>Create New Guest List</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}